// Catalog.ID Client — Registry Ceremony Consent Modal
// ============================================================================
// ONE dialog per ceremony, covering the WHOLE chain.
//
// Why one and not one-per-claim: a ceremony is a single decision — "rotate this
// account to a new key and re-issue my machines' grants" — that happens to be
// expressed as several claims. Asking N times would train the user to click
// through, and the Nth dialog is the one that matters. Ordinal pins mean the
// chain is atomic anyway: if the user could approve claim 1 and refuse claim 2
// the stream would be left half-rotated.
//
// Why semantics and not hashes: a person can compare a grantee fingerprint
// against the one their other machine shows, and can tell "grant register+store
// on every asset, no expiry" from "grant register on kind=chat until March".
// Nobody can meaningfully approve a SHA-512. The summaries come from
// `summarizeCeremonyClaim`, which is unit-tested against the golden vectors.
//
// No policy slot ('session' / 'always'). An authority-key ceremony is a
// high-stakes one-shot, exactly like registry key publication — the sibling
// modal `registry-key-consent-modal.ts` makes the same call for the same
// reason.
// ============================================================================

import type { Shell } from '@catalog-shell';
import { createFieldGroup, createFormRow, createElement } from '@catalog-shell';
import type { CeremonyClaimSummary } from './ceremony-claims';

export type CeremonyConsentDecision = 'approve' | 'deny';

export interface CeremonyConsentInput {
    /** The logged-in account whose keybag will sign. */
    owningCid: string;
    /** Hub-attested requester (pinned to `cpr` upstream; rendered, not trusted). */
    requesterId: string;
    ceremonyKind: string;
    ceremonyLabel?: string;
    /** The authority fingerprint the requester pinned. */
    expectedAuthorityFp: string;
    summaries: readonly CeremonyClaimSummary[];
    /** Parallel to `summaries`: which key signs each claim. */
    signerFingerprints: readonly string[];
}

const HEADINGS: Record<string, { title: string; blurb: string }> = {
    enrollment: {
        title: 'Create a Registry account stream?',
        blurb: 'This opens an account stream owned by your Catalog.ID authority key and '
            + 'grants a machine permission to act for it. From then on that account — not '
            + 'the machine — owns the assets it registers.',
    },
    rotation: {
        title: 'Rotate your Registry account authority key?',
        blurb: 'The stream moves to a new authority key held in this keybag. Every grant '
            + 'issued under the old key stops working the moment this is accepted, which is '
            + 'why the ceremony re-issues them in the same chain.',
    },
    successor: {
        title: 'Declare a successor key?',
        blurb: 'A successor may rebind assets owned by this account and may rotate the '
            + 'stream. It is loss protection, not theft protection.',
    },
    grant: {
        title: 'Sign a Registry account-stream ceremony?',
        blurb: 'Your account authority key will sign the claims listed below.',
    },
    revoke: {
        title: 'Revoke Registry delegate grants?',
        blurb: 'The listed grants stop authorising writes from the moment this is accepted. '
            + 'Claims already accepted under them stay valid.',
    },
    rebind: {
        title: 'Transfer Registry asset ownership?',
        blurb: 'Ownership of the listed assets moves. This is the one action a compromised '
            + 'machine can never perform on its own — check the target carefully.',
    },
};

export function showCeremonyConsent(
    shell: Shell,
    input: CeremonyConsentInput,
): Promise<CeremonyConsentDecision> {
    return new Promise<CeremonyConsentDecision>((resolve) => {
        let resolved = false;
        const resolveOnce = (d: CeremonyConsentDecision): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(d);
        };

        const heading = HEADINGS[input.ceremonyKind] ?? HEADINGS['grant']!;
        const container = createElement('div', { className: 'cs-entity-view cs-modal-body' });

        container.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: heading.title,
        }));

        // Account first, before any detail — the ceremony binds to WHOEVER
        // approves it. A ceremony approved under the wrong login signs with the
        // wrong authority key, and the resulting stream is unusable by the
        // machine's real owner. Same C7 finding the key-publication modal
        // records, and the same remedy: surface the owner.
        const accountGroup = createFieldGroup({ title: 'Signing as' });
        const accountRow = createFormRow({ label: 'Catalog.ID account' });
        accountRow.content.appendChild(createElement('code', {
            className: 'cs-field-view-text',
            textContent: input.owningCid,
        }));
        accountGroup.body.appendChild(accountRow.element);

        const authorityRow = createFormRow({ label: 'Authority key' });
        authorityRow.content.appendChild(createElement('code', {
            textContent: shortFp(input.expectedAuthorityFp),
            attributes: { title: input.expectedAuthorityFp },
        }));
        accountGroup.body.appendChild(authorityRow.element);

        const requesterRow = createFormRow({ label: 'Requested by' });
        requesterRow.content.appendChild(createElement('div', {
            className: 'cs-field-view-text',
            textContent: input.requesterId,
        }));
        accountGroup.body.appendChild(requesterRow.element);
        container.appendChild(accountGroup.element);

        container.appendChild(createElement('div', {
            className: 'cs-inline-warn',
            textContent: heading.blurb,
        }));

        if (input.ceremonyLabel) {
            container.appendChild(createElement('div', {
                style: { fontSize: '13px', opacity: '0.8', margin: '6px 0 2px' },
                textContent: input.ceremonyLabel,
            }));
        }

        // One group per claim, in submission order — the order is part of what
        // is being approved (each claim pins the ordinal after the previous).
        input.summaries.forEach((s, i) => {
            const ordinal = s.sequenceNumber !== undefined ? ` · ordinal ${s.sequenceNumber}` : '';
            const group = createFieldGroup({
                title: `${i + 1}. ${s.action}`,
            });

            const typeRow = createFormRow({ label: 'Claim' });
            typeRow.content.appendChild(createElement('div', {
                className: 'cs-field-view-text',
                textContent: `${s.claimType}${ordinal}`,
            }));
            group.body.appendChild(typeRow.element);

            const streamRow = createFormRow({ label: 'On' });
            streamRow.content.appendChild(createElement('code', {
                textContent: `${s.subject} @ ${s.operatorId}`,
            }));
            group.body.appendChild(streamRow.element);

            for (const d of s.details) {
                const row = createFormRow({ label: d.label });
                row.content.appendChild(createElement('div', {
                    className: 'cs-field-view-text',
                    textContent: d.value,
                }));
                group.body.appendChild(row.element);
            }

            const signer = input.signerFingerprints[i];
            if (signer) {
                const row = createFormRow({ label: 'Signed by' });
                row.content.appendChild(createElement('code', {
                    textContent: shortFp(signer),
                    attributes: { title: signer },
                }));
                group.body.appendChild(row.element);
            }

            container.appendChild(group.element);
        });

        const actions = createElement('div', { className: 'cs-form-actions' });
        const btnDeny = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Deny',
        }) as HTMLButtonElement;
        btnDeny.addEventListener('click', () => resolveOnce('deny'));
        actions.appendChild(btnDeny);

        const btnApprove = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: input.summaries.length === 1 ? 'Sign' : `Sign ${input.summaries.length} claims`,
        }) as HTMLButtonElement;
        btnApprove.addEventListener('click', () => resolveOnce('approve'));
        actions.appendChild(btnApprove);
        container.appendChild(actions);

        const handle = shell.showModal(container, {
            size: 'wide',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            // A dismissed dialog is a refusal. Never a silent approval, and
            // never a hang: the requester is waiting on this promise.
            onClose: () => resolveOnce('deny'),
        });
    });
}

function shortFp(fp: string): string {
    return fp.length <= 24 ? fp : `${fp.slice(0, 8)}…${fp.slice(-8)}`;
}
